'use client'

import { PaginationProviderProps } from '../types/Pagination'
import React, { useMemo, createContext, useContext, useState, useEffect, useRef } from 'react'
import { FilterContext } from './filter-provider'
import { SortingContext } from './SortingProvider'
import { Filter } from '../types/Filters'
import { PaginationParams, PaginationProviderModel } from '../components'
import {
    useInitialPaginationFromURL,
    usePaginationQueryParamsInURL,
} from '../hooks/UsePaginationQueryParamsInURL'
import { stringExists } from '../../../MyStichtingOpenHelpers/src/static'

/**
 * Only filters that actually carry a value influence the result set. Filters register themselves
 * after mount, so comparing the raw array would reset the page on every registration.
 */
const getActiveFiltersSignature = (filters: Filter[] | undefined): string =>
    JSON.stringify(
        (filters ?? [])
            .filter((filter) => stringExists(filter.value))
            .map((filter) => [filter.id, filter.value])
            .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    )

export const PaginationContext = createContext<PaginationProviderModel>({} as PaginationProviderModel)

export const PaginationProvider = ({
    children,
    offset = 0,
    limit = 25,
    total = 0,
    initialPage = 1,
    bindToURL = true,
}: PaginationProviderProps) => {
    const filterContext = useContext(FilterContext)
    const sortingContext = useContext(SortingContext)

    const initialPagination = useInitialPaginationFromURL({ bindToURL, offset, limit, initialPage })

    const [params, setParams] = useState<PaginationParams>(initialPagination.params)
    const [totalResults, setTotalResults] = useState<number>(total)
    const [currentPage, setCurrentPage] = useState<number>(initialPagination.page)

    const paginateHandler = (offset: number, limit: number): void => {
        const sameConfig: boolean = params.offset === offset && params.limit === limit
        if (sameConfig) return
        setParams({ offset, limit })
    }

    const resetPagination = () => {
        paginateHandler(0, params.limit)
        setCurrentPage(1)
    }

    const activeFiltersSignature = getActiveFiltersSignature(filterContext?.filters)
    const sortingSignature = `${sortingContext?.sorting?.sortBy ?? ''}|${sortingContext?.sorting?.direction ?? ''}`

    // Seeded with the mount values so a page restored from the URL is not reset on the first run.
    const previousFiltersSignature = useRef<string>(activeFiltersSignature)
    const previousSortingSignature = useRef<string>(sortingSignature)

    useEffect(() => {
        if (!filterContext) return
        if (previousFiltersSignature.current === activeFiltersSignature) return
        previousFiltersSignature.current = activeFiltersSignature
        resetPagination()
    }, [activeFiltersSignature])

    useEffect(() => {
        if (!sortingContext) return
        if (previousSortingSignature.current === sortingSignature) return
        previousSortingSignature.current = sortingSignature
        resetPagination()
    }, [sortingSignature])

    usePaginationQueryParamsInURL({
        bindToURL,
        initialPage,
        limit,
        page: currentPage,
        perPage: params.limit,
    })

    const PaginationProviderData: PaginationProviderModel = useMemo(
        () => ({
            params,
            totalResults,
            setTotalResults,
            paginateHandler,
            initialPage,
            resetPagination,
            currentPage,
            setCurrentPage,
        }),
        [params, totalResults, initialPage, currentPage],
    )

    return <PaginationContext.Provider value={PaginationProviderData}>{children}</PaginationContext.Provider>
}
